import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { USER_TYPES, USER_TYPE_BY_VALUE } from '../lib/userTypes';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('public');
  const [loading, setLoading] = useState(false);

  const selectedType = USER_TYPE_BY_VALUE[userType];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const newUser = await register(email, password, name, userType);
      if (newUser.status === 'pending') {
        toast.success('Account created — awaiting admin approval');
      } else {
        toast.success('Welcome to VINEYARDS & WINE!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-stone-950">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-300 mb-12 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <img 
            src="https://customer-assets.emergentagent.com/job_winery-finder/artifacts/i4imw511_Vineyards%26Wine%20%281%29.png" 
            alt="VINEYARDS & WINE" 
            className="w-[200px] h-[120px] object-contain mb-8"
          />

          <h1 className="text-3xl font-serif text-stone-50 mb-2">Join the Community</h1>
          <p className="text-stone-500 mb-8">Create your account and start exploring</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-stone-500">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-transparent border-0 border-b border-stone-700 rounded-none px-0 py-4 focus:border-[#d4af37] focus:ring-0 placeholder:text-stone-600 text-lg"
                placeholder="John Doe"
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-stone-500">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-transparent border-0 border-b border-stone-700 rounded-none px-0 py-4 focus:border-[#d4af37] focus:ring-0 placeholder:text-stone-600 text-lg"
                placeholder="your@email.com"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-stone-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="bg-transparent border-0 border-b border-stone-700 rounded-none px-0 py-4 focus:border-[#d4af37] focus:ring-0 placeholder:text-stone-600 text-lg"
                placeholder="••••••••"
                data-testid="password-input"
              />
              <p className="text-xs text-stone-600">Minimum 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-type" className="text-xs uppercase tracking-widest text-stone-500">
                I am a...
              </Label>
              <Select value={userType} onValueChange={setUserType}>
                <SelectTrigger
                  id="user-type"
                  className="bg-transparent border-0 border-b border-stone-700 rounded-none px-0 py-5 h-auto text-lg focus:border-[#d4af37] focus:ring-0 [&>span]:text-left"
                  data-testid="user-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700">
                  {USER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`user-type-option-${t.value}`}>
                      <div className="flex flex-col">
                        <span>{t.label}</span>
                        <span className="text-xs text-stone-500">{t.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType && !selectedType.autoApproved && (
                <p className="text-xs text-[#d4af37] mt-2">
                  <span className="font-semibold">Note:</span> This account type requires admin approval before you can participate fully.
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest font-bold text-xs py-6 mt-8"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-stone-500">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-[#d4af37] hover:underline"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1673227615351-029f0a26e311?w=1200&q=80"
          alt="Wine tasting"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-stone-950/80 to-stone-950/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-2xl font-serif text-stone-200 italic mb-4">
            "In wine there is wisdom, in beer there is freedom, in water there is bacteria."
          </blockquote>
          <p className="text-stone-400">— Benjamin Franklin</p>
        </div>
      </div>
    </div>
  );
};

export default Register;
